function normalizeOptionalString(value) {
  if (value === undefined || value === null) {
    return null;
  }

  const normalizedValue = String(value).trim();
  return normalizedValue || null;
}

function normalizeFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function normalizeUppercaseString(value) {
  const normalizedValue = normalizeOptionalString(value);
  return normalizedValue ? normalizedValue.toUpperCase() : null;
}

const POLLUTANT_LABELS = Object.freeze({
  c6h6: "Benzene",
  co: "CO",
  nh3: "NH3",
  nmhc: "NMHC",
  no: "NO",
  no2: "NO2",
  nox: "NOx",
  o3: "O3",
  ox: "OX",
  p25: "PM2.5",
  pm25: "PM2.5",
  pm10: "PM10",
  so2: "SO2",
  trs: "TRS",
});
const POLLUTANT_SORT_ORDER = Object.freeze([
  "p25",
  "pm25",
  "pm10",
  "no2",
  "o3",
  "so2",
  "co",
  "no",
  "nox",
  "nh3",
  "nmhc",
  "ox",
  "trs",
  "c6h6",
]);
const UNIT_LABELS = Object.freeze({
  CELSIUS: "C",
  FAHRENHEIT: "F",
  INDEX: "index",
  MICROGRAMS_PER_CUBIC_METER: "ug/m3",
  MILLIBARS: "mb",
  PARTS_PER_BILLION: "ppb",
  PERCENT: "%",
});
const WEATHER_DETAIL_VARIABLES = new Set([
  "temperature",
  "relative_humidity",
  "atmospheric_pressure",
]);

function findObservation(observations, variableNames) {
  return observations.find((entry) =>
    variableNames.includes(normalizeOptionalString(entry?.variable)),
  ) || null;
}

function formatObservationValue(observation) {
  const value = normalizeFiniteNumber(observation?.value);
  if (value === null) {
    return null;
  }

  const roundedValue = Math.round(value * 10) / 10;
  const normalizedUnit = normalizeUppercaseString(observation?.unit);
  const unit =
    UNIT_LABELS[normalizedUnit] ||
    normalizeOptionalString(observation?.unit);
  return unit ? `${roundedValue} ${unit}` : String(roundedValue);
}

function normalizeVariableName(value) {
  return normalizeOptionalString(value)?.toLowerCase() || null;
}

function formatVariableLabel(variable) {
  const normalizedVariable = normalizeVariableName(variable);
  if (!normalizedVariable) {
    return null;
  }

  if (POLLUTANT_LABELS[normalizedVariable]) {
    return POLLUTANT_LABELS[normalizedVariable];
  }

  if (normalizedVariable === "atmospheric_pressure") {
    return "Pressure";
  }

  return normalizedVariable.toUpperCase();
}

function collectPollutantObservations(observations) {
  const pollutantEntries = observations.filter((entry) => {
    const variable = normalizeVariableName(entry?.variable);
    return variable && Object.prototype.hasOwnProperty.call(POLLUTANT_LABELS, variable);
  });

  return pollutantEntries.sort((left, right) => {
    const leftVariable = normalizeVariableName(left?.variable);
    const rightVariable = normalizeVariableName(right?.variable);
    const leftIndex = POLLUTANT_SORT_ORDER.indexOf(leftVariable);
    const rightIndex = POLLUTANT_SORT_ORDER.indexOf(rightVariable);

    if (leftIndex !== rightIndex) {
      return (
        (leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex) -
        (rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex)
      );
    }

    return String(formatVariableLabel(leftVariable) || "").localeCompare(
      String(formatVariableLabel(rightVariable) || ""),
    );
  });
}

function formatCountryLabel(countryCode, countryName) {
  const normalizedCountryCode = normalizeUppercaseString(countryCode);
  if (normalizedCountryCode === "GB") {
    return "UK";
  }

  return normalizeOptionalString(countryName) || normalizedCountryCode;
}

function buildLocationDisplayName(location = null) {
  if (!location || typeof location !== "object" || Array.isArray(location)) {
    return null;
  }

  const explicitDisplayName = normalizeOptionalString(location.displayName);
  if (explicitDisplayName) {
    return explicitDisplayName;
  }

  const locality = normalizeOptionalString(location.locality);
  const countryLabel = formatCountryLabel(
    normalizeOptionalString(location.countryCode) ||
      normalizeOptionalString(location.regionCode),
    location.countryName,
  );

  if (locality && countryLabel) {
    return `${locality}, ${countryLabel}`;
  }

  return locality || countryLabel || null;
}

export function getEnvironmentalContextSnapshot(
  context,
  { includeWeatherDetails = true } = {},
) {
  if (!context || typeof context !== "object" || Array.isArray(context)) {
    return null;
  }

  const rawObservations = Array.isArray(context.observations)
    ? context.observations
    : [];
  const observations = includeWeatherDetails
    ? rawObservations
    : rawObservations.filter(
        (entry) =>
          !WEATHER_DETAIL_VARIABLES.has(
            normalizeOptionalString(entry?.variable),
          ),
      );
  const aqi = findObservation(observations, ["aqi"]);
  const temperature = findObservation(observations, ["temperature"]);
  const humidity = findObservation(observations, ["relative_humidity"]);
  const pressure = findObservation(observations, ["atmospheric_pressure"]);
  const pollen =
    findObservation(observations, ["tree_pollen_index"]) ||
    findObservation(observations, ["grass_pollen_index"]) ||
    findObservation(observations, ["weed_pollen_index"]);
  const pollutants = collectPollutantObservations(observations);
  const primaryObservation =
    aqi || temperature || humidity || pressure || pollen || observations[0] || null;

  const summaryParts = [
    aqi ? `AQI ${formatObservationValue(aqi)}` : null,
    temperature ? `Temp ${formatObservationValue(temperature)}` : null,
    humidity ? `Humidity ${formatObservationValue(humidity)}` : null,
    pressure ? `Pressure ${formatObservationValue(pressure)}` : null,
    pollen ? `Pollen ${formatObservationValue(pollen)}` : null,
  ].filter(Boolean);
  const supplementalSummaryParts = pollutants
    .slice(0, 2)
    .map((observation) => {
      const label = formatVariableLabel(observation?.variable);
      const value = formatObservationValue(observation);
      return label && value ? `${label} ${value}` : null;
    })
    .filter(Boolean);

  return {
    summary: summaryParts.join(" | ") || null,
    supplementalSummary: supplementalSummaryParts.join(" | ") || null,
    locationDisplayName: buildLocationDisplayName(context.location),
    metrics: {
      aqi,
      temperature,
      humidity,
      pressure,
      pollen,
    },
    pollutants: pollutants.map((observation) => ({
      ...observation,
      label: formatVariableLabel(observation?.variable),
      formattedValue: formatObservationValue(observation),
    })),
    status:
      normalizeOptionalString(primaryObservation?.qualityStatus) ||
      normalizeOptionalString(context.status),
    source: normalizeOptionalString(primaryObservation?.source),
    observationTimestamp: normalizeOptionalString(
      primaryObservation?.observationTimestamp,
    ),
    locationGranularity:
      normalizeOptionalString(context.location?.granularity) ||
      normalizeOptionalString(primaryObservation?.locationGranularity),
    limitations: Array.isArray(context.limitations)
      ? context.limitations
          .map((entry) => normalizeOptionalString(entry))
          .filter(Boolean)
      : [],
  };
}
